import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext(undefined);

const THEME_KEY = 'calendiary-theme';

// Valid theme values
const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(THEMES.SYSTEM);
  const [resolvedTheme, setResolvedTheme] = useState('dark');
  const [mounted, setMounted] = useState(false);

  // Get the actual theme based on system preference
  const getResolvedTheme = useCallback((themeValue) => {
    if (themeValue === THEMES.SYSTEM) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? THEMES.DARK
        : THEMES.LIGHT;
    }
    return themeValue;
  }, []);

  // Apply theme to document
  const applyTheme = useCallback((themeValue) => {
    const resolved = getResolvedTheme(themeValue);
    setResolvedTheme(resolved);

    const root = document.documentElement;

    if (resolved === THEMES.DARK) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
    }
  }, [getResolvedTheme]);

  // Initialize theme on mount
  useEffect(() => {
    const stored = localStorage.getItem(THEME_KEY);
    const initialTheme = stored && Object.values(THEMES).includes(stored)
      ? stored
      : THEMES.SYSTEM;

    setThemeState(initialTheme);
    applyTheme(initialTheme);
    setMounted(true);
  }, [applyTheme]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      if (theme === THEMES.SYSTEM) {
        applyTheme(THEMES.SYSTEM);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, applyTheme]);

  // Set theme function
  const setTheme = useCallback((newTheme) => {
    if (!Object.values(THEMES).includes(newTheme)) {
      console.warn(`Invalid theme: ${newTheme}. Using system.`);
      newTheme = THEMES.SYSTEM;
    }

    setThemeState(newTheme);
    localStorage.setItem(THEME_KEY, newTheme);
    applyTheme(newTheme);
  }, [applyTheme]);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    const newTheme = resolvedTheme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
    setTheme(newTheme);
  }, [resolvedTheme, setTheme]);

  const value = {
    theme,           // Current theme setting (light/dark/system)
    resolvedTheme,   // Actual theme being displayed (light/dark)
    setTheme,        // Set theme function
    toggleTheme,     // Toggle between light/dark
    mounted,         // Whether the theme has been initialized
    isDark: resolvedTheme === THEMES.DARK,
    isLight: resolvedTheme === THEMES.LIGHT,
    isSystem: theme === THEMES.SYSTEM,
    THEMES,          // Export theme constants
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export { THEMES };
export default ThemeContext;
